import React, { useEffect, useState, useRef } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Grid,
  Typography,
  Box,
  Autocomplete,
} from "@mui/material";
import { ref, onValue } from "firebase/database";
import { db } from "../../firebaseConfig";
import { generateFichaCaptacaoPDF } from "../Components/generateFichaCaptacao.js";
import Logo from "../../assets/logo1.png";

import "../styles/fichacaptacao.css";

const FichaCaptacao = () => {
  const [corretores, setCorretores] = useState([]);
  const [imoveis, setImoveis] = useState([]);
  const [corretorSelecionado, setCorretorSelecionado] = useState("");
  const [cliente, setCliente] = useState("");
  const [imovelSelecionado, setImovelSelecionado] = useState(null);
  const [allAtributos, setAllAtributos] = useState([]);
  const pdfRef = useRef();

  // --- Busca Corretores ---
  useEffect(() => {
    const corretoresRef = ref(db, "corretores");
    onValue(corretoresRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const lista = Object.entries(data).map(([id, v]) => ({
          id,
          nome: v.nome,
          creci: v.creci || "-",
        }));
        setCorretores(lista);
      }
    });
  }, []);

  // --- Busca Imóveis ---
  useEffect(() => {
    const imoveisRef = ref(db, "imoveis");
    onValue(imoveisRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const lista = Object.entries(data).map(([id, item]) => ({
          id,
          codigo: item.imovelInfo?.codigo || "Sem código",
          titulo: item.imovelInfo?.titulo || "",
          endereco: `${item.imovelInfo?.bairro || ""}, ${item.imovelInfo?.cidade || ""
            }`,
          imovelInfo: item.imovelInfo || {},
          informacoesComerciais: item.informacoesComerciais || {},
          informacoesAdicionais: item.informacoesAdicionais || {},
          selectedItems: item.selectedItems || [],
          ownerInfo: item.ownerInfo || {},
        }));
        setImoveis(lista);
      }
    });
  }, []);

  // --- Busca Atributos ---
  useEffect(() => {
    const atributosRef = ref(db, "atributos");
    onValue(atributosRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setAllAtributos(Object.values(data));
      } else {
        setAllAtributos([]);
      }
    });
  }, []);

  // --- Gerar PDF ---
  const handlePrint = () => {
    if (!cliente || !corretorSelecionado) {
      alert("Preencha o nome do cliente e o corretor responsável.");
      return;
    }

    // mesmo sem imóvel, pode gerar a ficha
    generateFichaCaptacaoPDF({
      imovel: imovelSelecionado || null,
      cliente,
      corretor: corretorSelecionado,
      allAtributos,
    });
  };

  return (
    <div className="fichaCaptacaoPage">
      <Typography variant="h5" gutterBottom mb={5} ml={34}>
        Ficha de Captação
      </Typography>

      <Grid container spacing={2} ml={32}>
        {/* Corretor */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Corretor Responsável"
            fullWidth
            value={corretorSelecionado?.nome || ""}
            onChange={(e) => {
              const selected = corretores.find((c) => c.nome === e.target.value);
              setCorretorSelecionado(selected || null);
            }}
          >
            {corretores.map((corretor) => (
              <MenuItem key={corretor.id} value={corretor.nome}>
                {corretor.nome}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Cliente */}
        <Grid item xs={12} sm={6}>
          <TextField
            required
            label="Nome do Cliente"
            fullWidth
            value={cliente}
            onChange={(e) => setCliente(e.target.value)}
          />
        </Grid>

        {/* Imóvel */}
        <Grid item xs={12} sm={12}>
          <Autocomplete
            options={imoveis}
            getOptionLabel={(option) =>
              `${option.codigo} - ${option.endereco}`
            }
            value={imovelSelecionado}
            onChange={(e, val) => setImovelSelecionado(val)}
            renderInput={(params) => (
              <TextField {...params} label="Selecionar Imóvel" fullWidth />
            )}
          />
        </Grid>
      </Grid>

      <Box mt={3} ml={34}>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "var(--verde)",
            color: "#fff",
            "&:hover": {
              backgroundColor: "var(--bege)",
            },
          }}
          onClick={handlePrint}
        >
          Imprimir
        </Button>
      </Box>

      {/* Template PDF estilizado */}
      <div
        ref={pdfRef}
        style={{
          display: "none",
          width: "595px",
          padding: "40px 50px",
          backgroundColor: "white",
          color: "#000",
          fontFamily: "Arial, sans-serif",
          fontSize: "11pt",
          lineHeight: "1.4",
        }}
      >
        <Box textAlign="center" mb={1}>
          <img
            src={Logo}
            alt="Logo"
            style={{ width: "130px", marginBottom: "5px" }}
          />
          <Typography
            variant="subtitle2"
            style={{
              fontSize: "10pt",
              color: "#333",
              fontWeight: 500,
            }}
          >
            Senna Viannini - CRECI PJ: 9568
            <br />
            São João Del Rei - MG
            <br />
            (32) 99900-3833 | www.sennaviannini.com.br
          </Typography>
        </Box>

        <hr style={{ margin: "10px 0 20px 0" }} />

        <Typography
          align="center"
          fontWeight="bold"
          variant="h6"
          style={{ fontSize: "12pt" }}
        >
          Ficha de Captação
        </Typography>

        {imovelSelecionado && (
          <Box mt={2}>
            <Typography paragraph style={{ fontSize: "10pt" }}>
              <b>Imóvel Cód.:</b> {imovelSelecionado.codigo} <br />
              <b>Localização:</b> {imovelSelecionado.endereco}
            </Typography>
          </Box>
        )}

        <Typography paragraph style={{ fontSize: "10pt", textAlign: "justify" }}>
          Declaro que realizei a captação do imóvel acima descrito, de acordo com
          as informações fornecidas pelo cliente e com base em vistoria
          realizada, estando ciente das condições atuais do bem e da
          documentação correspondente.
        </Typography>

        <Typography paragraph align="right" style={{ fontSize: "10pt" }}>
          São João Del Rei, {new Date().toLocaleDateString("pt-BR")}
        </Typography>

        <Typography style={{ fontSize: "10pt" }}>
          <b>Cliente:</b> {cliente} <br />
          <b>Corretor:</b> {corretorSelecionado?.nome || "-"} <br />
          <b>CRECI:</b> {corretorSelecionado?.creci || "-"}
        </Typography>

        <Box mt={6}>
          <Typography align="center" style={{ fontSize: "10pt" }}>
            _______________________________________________
          </Typography>
          <Typography align="center" style={{ fontSize: "9pt" }}>
            Assinatura do Cliente
          </Typography>

          <Box mt={4}>
            <Typography align="center" style={{ fontSize: "10pt" }}>
              _______________________________________________
            </Typography>
            <Typography align="center" style={{ fontSize: "9pt" }}>
              Corretor Responsável ({corretorSelecionado?.nome || "-"})
            </Typography>
          </Box>
        </Box>
      </div>
    </div>
  );
};

export default FichaCaptacao;
